import React, { useEffect, useState } from "react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Cell,
} from "recharts";
import { AlertTriangle, Flame, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { formatINR, formatCount, formatLitres, formatPct } from "@/utils/formatters";
import PageHeader from "@/components/layout/PageHeader";
import GlassCard from "@/components/ui-fleet/GlassCard";
import ClickableKPITile from "@/components/ui-fleet/ClickableKPITile";
import DrillDownPanel from "@/components/ui-fleet/DrillDownPanel";
import StatTableDrillDown from "@/components/ui-fleet/StatTableDrillDown";

const EFF_COLORS = { green: "#2D7A4A", amber: "#B07D1A", red: "#B03A2A" };

export default function FuelEnergy() {
  const [data, setData] = useState(null);
  const [idleHrs, setIdleHrs] = useState(2.5);
  const [drill, setDrill] = useState(null);

  useEffect(() => { api.get("/fuel/analytics").then((r) => setData(r.data)); }, []);
  if (!data) return <div><PageHeader title="Fuel & Energy" breadcrumb="Operations" /><div className="text-center py-20" style={{ color: "var(--slate-500)" }}>Loading…</div></div>;

  const k = data.kpis;

  // Idle waste calculator
  const litresPerDay = idleHrs * 4 * 100;  // 4 L/hr × 100 machines
  const rsPerMonth = litresPerDay * 30 * 95;
  const rsPerYear = rsPerMonth * 12;
  const savings30 = rsPerYear * 0.3;

  return (
    <div data-testid="fuel-energy">
      <PageHeader title="Fuel & Energy" breadcrumb="Operations" />

      {/* Banner */}
      <GlassCard variant="accent" className="p-4 mb-5 flex items-center gap-3" data-testid="anomaly-banner">
        <AlertTriangle size={22} style={{ color: "var(--status-amber)" }} />
        <div className="flex-1">
          <div className="font-medium text-[14px]" style={{ color: "var(--slate-900)" }}>
            {data.anomalies.length} machines showing fuel anomaly patterns — potential leak or theft detected
          </div>
          <div className="text-[12px]" style={{ color: "var(--slate-700)" }}>
            AI cross-referenced consumption vs GPS movement. Top suspect: <strong>EQP-056 (94% confidence)</strong>
          </div>
        </div>
        <button className="btn-copper" data-testid="review-anomalies">Review Anomalies</button>
      </GlassCard>

      <div className="grid grid-cols-5 gap-4 mb-5">
        <ClickableKPITile label="Fuel Today" value={formatCount(k.total_fuel_today_l)} suffix="L" sub="Tap for top consumers" color="copper" onClick={() => setDrill("today")} testId="kpi-fuel-today" />
        <ClickableKPITile label="Idle Waste" value={formatLitres(k.waste_l)} sub={`${formatINR(k.waste_rs, { compact: true })} · tap for wasters`} color="amber" onClick={() => setDrill("waste")} testId="kpi-idle-waste" />
        <ClickableKPITile label="Anomaly Machines" value={formatCount(k.anomaly_count)} sub="Flagged by AI · tap to review" color="red" onClick={() => setDrill("anomalies")} testId="kpi-anomalies" />
        <ClickableKPITile label="Energy Index" value={formatCount(k.energy_index)} suffix="/100" sub="Tap for hour-of-day peaks" color="green" onClick={() => setDrill("energy")} testId="kpi-energy" />
        <ClickableKPITile label="Cost / HMR" value={formatINR(k.cost_per_hmr)} sub="Tap for per-category breakdown" color="slate" onClick={() => setDrill("cost")} testId="kpi-cost" />
      </div>

      <div className="grid grid-cols-12 gap-5 mb-5">
        <GlassCard className="col-span-7 p-5">
          <h3 className="font-display text-[16px] mb-1" style={{ color: "var(--slate-900)" }}>Fuel Consumption vs Threshold</h3>
          <p className="text-[12px] mb-3" style={{ color: "var(--slate-500)" }}>30-day fleet total · shaded zone above threshold = waste</p>
          <div style={{ height: 280 }}>
            <ResponsiveContainer>
              <AreaChart data={data.fuel_series}>
                <defs>
                  <linearGradient id="conG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C4622D" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#C4622D" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="wasteG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#B03A2A" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#B03A2A" stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(184,175,164,0.25)" />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} />
                <YAxis />
                <Tooltip formatter={(v) => `${v} L`} />
                <ReferenceLine y={4500} stroke="#B03A2A" strokeDasharray="4 4" label={{ value: "Threshold", fontSize: 10, fill: "#B03A2A" }} />
                <Area dataKey="consumption" stroke="#C4622D" strokeWidth={2} fill="url(#conG)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>

        <GlassCard className="col-span-5 p-5">
          <h3 className="font-display text-[16px] mb-1" style={{ color: "var(--slate-900)" }}>Efficiency by Category (L/hr)</h3>
          <p className="text-[12px] mb-3" style={{ color: "var(--slate-500)" }}>Lower is better</p>
          <div style={{ height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={data.efficiency_by_category} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(184,175,164,0.25)" />
                <XAxis type="number" />
                <YAxis dataKey="category" type="category" width={50} />
                <Tooltip formatter={(v) => `${v} L/hr`} />
                <Bar dataKey="lph" radius={[0, 6, 6, 0]}>
                  {data.efficiency_by_category.map((d, i) => (
                    <Cell key={i} fill={EFF_COLORS[d.status]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>
      </div>

      <div className="grid grid-cols-12 gap-5 mb-5">
        {/* Anomalies */}
        <GlassCard variant="accent" className="col-span-5 p-5" data-testid="anomaly-panel">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} style={{ color: "var(--accent)" }} />
            <h3 className="font-display text-[16px]" style={{ color: "var(--slate-900)" }}>AI Anomaly Detection — Fuel</h3>
          </div>
          <div className="space-y-3">
            {data.anomalies.map((a) => (
              <div key={a.machine_id} className="glass-card-sm p-3" data-testid={`anomaly-${a.machine_id}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="font-mono text-[13px] font-medium" style={{ color: "var(--slate-900)" }}>{a.machine_id}</span>
                  <span className="status-pill red">{a.above_baseline_pct}% above</span>
                </div>
                <p className="text-[12px]" style={{ color: "var(--slate-700)" }}>{a.cause}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[11px]" style={{ color: "var(--slate-500)" }}>Confidence <span className="font-mono">{a.confidence}%</span></span>
                  <div className="flex gap-2">
                    <button className="btn-ghost text-[11px] py-1 px-2" data-testid={`raise-${a.machine_id}`}>Raise Alert</button>
                    <button className="btn-copper text-[11px] py-1 px-2" data-testid={`assign-${a.machine_id}`}>Assign</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>

        {/* Idle waste calculator */}
        <GlassCard className="col-span-7 p-5" data-testid="idle-calculator">
          <div className="flex items-center gap-2 mb-1">
            <Flame size={16} style={{ color: "var(--accent)" }} />
            <h3 className="font-display text-[16px]" style={{ color: "var(--slate-900)" }}>Idle Fuel Waste Calculator</h3>
          </div>
          <p className="text-[12px] mb-5" style={{ color: "var(--slate-500)" }}>
            Move the slider — see your annual savings opportunity
          </p>
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[12px]" style={{ color: "var(--slate-700)" }}>Avg idle hours / day per machine</span>
              <span className="font-display text-[22px]" style={{ color: "var(--accent)" }}>{idleHrs.toFixed(1)}h</span>
            </div>
            <input
              type="range" min="0" max="6" step="0.1" value={idleHrs}
              onChange={(e) => setIdleHrs(parseFloat(e.target.value))}
              className="w-full accent-orange-700"
              style={{ accentColor: "var(--accent)" }}
              data-testid="idle-slider"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Stat label="Litres / day" value={formatLitres(Math.round(litresPerDay))} />
            <Stat label="₹ / month" value={formatINR(Math.round(rsPerMonth), { compact: true })} />
            <Stat label="₹ / year" value={formatINR(Math.round(rsPerYear), { compact: true })} accent />
          </div>

          <div className="glass-card-sm p-4 mt-5 flex items-center gap-3" style={{ background: "rgba(45, 122, 74, 0.06)" }}>
            <span className="status-pill green">Save</span>
            <p className="text-[13px]" style={{ color: "var(--slate-900)" }}>
              Reduce idle by 30% → save <strong style={{ color: "var(--status-green)" }}>{formatINR(Math.round(savings30), { compact: true })}</strong> per year
            </p>
          </div>
        </GlassCard>
      </div>

      {/* Energy Heatmap */}
      <GlassCard className="p-5">
        <h3 className="font-display text-[16px] mb-1" style={{ color: "var(--slate-900)" }}>Energy Consumption Heatmap</h3>
        <p className="text-[12px] mb-4" style={{ color: "var(--slate-500)" }}>7 days × 24 hours · identify peak windows</p>
        <EnergyHeatmap data={data.energy_heatmap} />
      </GlassCard>

      <DrillDownPanel
        isOpen={drill !== null}
        onClose={() => setDrill(null)}
        title={
          drill === "today" ? "Today's Fuel Burn"
          : drill === "waste" ? "Idle Fuel Waste"
          : drill === "anomalies" ? "AI Fuel Anomalies"
          : drill === "energy" ? "Energy Heatmap Peaks"
          : drill === "cost" ? "Cost Per HMR by Category"
          : ""
        }
        subtitle={
          drill === "today" ? `${formatLitres(k.total_fuel_today_l)} fleet-wide today`
          : drill === "waste" ? `${formatLitres(k.waste_l)} lost · ${formatINR(k.waste_rs, { compact: true })}`
          : drill === "anomalies" ? `${data.anomalies.length} flagged by AI`
          : drill === "energy" ? "Top-load weekday & hour combinations"
          : drill === "cost" ? "L/hr × ₹95 fuel rate × duty cycle"
          : ""
        }
        testId="fuel-drilldown"
      >
        {drill === "today" && (
          <StatTableDrillDown
            rows={data.efficiency_by_category.map((c) => ({
              category: c.category,
              lph: c.lph,
              cost_per_hr: Math.round(c.lph * 95),
              status: c.status,
            }))}
            columns={[
              { key: "category", label: "Category", mono: true },
              { key: "lph", label: "L/hr", format: (v) => `${v}` },
              { key: "cost_per_hr", label: "₹/hr", format: (v) => formatINR(v) },
              { key: "status", label: "Efficiency" },
            ]}
          />
        )}

        {drill === "waste" && (
          <div>
            <p className="font-body text-sm" style={{ color: "var(--slate-700)", marginBottom: 12 }}>
              {formatLitres(k.waste_l)} of idle-burn waste today × ₹95/L ≈ {formatINR(k.waste_rs, { compact: true })}.
              Top categories below — drill into Fleet Monitor for per-machine view.
            </p>
            <StatTableDrillDown
              rows={data.efficiency_by_category.filter((c) => c.status !== "green")}
              columns={[
                { key: "category", label: "Category", mono: true },
                { key: "lph", label: "L/hr" },
                { key: "status", label: "Flag" },
              ]}
              emptyText="All categories within green threshold."
            />
          </div>
        )}

        {drill === "anomalies" && (
          <StatTableDrillDown
            rows={data.anomalies}
            columns={[
              { key: "machine_id", label: "Machine", mono: true },
              { key: "above_baseline_pct", label: "Δ vs baseline", format: (v) => formatPct(v, 0) },
              { key: "cause", label: "Cause" },
              { key: "confidence", label: "Confidence", format: (v) => formatPct(v, 0) },
            ]}
          />
        )}

        {drill === "energy" && (
          <StatTableDrillDown
            rows={[...data.energy_heatmap].sort((a, b) => b.value - a.value).slice(0, 15)}
            columns={[
              { key: "day", label: "Day" },
              { key: "hour", label: "Hour", format: (v) => `${v}:00` },
              { key: "value", label: "Load", format: (v) => formatPct(v, 0) },
            ]}
          />
        )}

        {drill === "cost" && (
          <StatTableDrillDown
            rows={data.efficiency_by_category.map((c) => ({
              category: c.category,
              lph: c.lph,
              cost: Math.round(c.lph * 95),
              monthly: Math.round(c.lph * 95 * 8 * 25),
            }))}
            columns={[
              { key: "category", label: "Category", mono: true },
              { key: "lph", label: "L/hr" },
              { key: "cost", label: "₹/hr", format: (v) => formatINR(v) },
              { key: "monthly", label: "₹/month (8h×25d)", format: (v) => formatINR(v, { compact: true }) },
            ]}
          />
        )}
      </DrillDownPanel>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="glass-card-sm p-3">
      <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--slate-500)" }}>{label}</div>
      <div className="font-display text-[20px]" style={{ color: accent ? "var(--accent)" : "var(--slate-900)" }}>{value}</div>
    </div>
  );
}

function EnergyHeatmap({ data }) {
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const hours = [...Array(24).keys()];
  const lookup = {};
  data.forEach((d) => { lookup[`${d.day}-${d.hour}`] = d.value; });
  const cellColor = (v) => {
    const t = Math.min(1, v / 90);
    return `rgba(196, 98, 45, ${0.08 + t * 0.55})`;
  };
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[10px] border-separate" style={{ borderSpacing: "2px" }}>
        <thead>
          <tr>
            <th></th>
            {hours.map((h) => (
              <th key={h} className="font-medium" style={{ color: "var(--slate-500)" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {days.map((d) => (
            <tr key={d}>
              <td className="pr-2 text-right" style={{ color: "var(--slate-500)" }}>{d}</td>
              {hours.map((h) => {
                const v = lookup[`${d}-${h}`] || 0;
                return (
                  <td key={h} title={`${d} ${h}:00 — ${v}%`}>
                    <div className="rounded-sm" style={{ background: cellColor(v), height: 20, minWidth: 14 }} />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
